import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client/react';
import { Button } from '../../shared/components/button';
import { Modal } from '../../shared/components/modal';
import { TextInput } from '../../shared/components/text-input';
import { ValidationMessage } from '../../shared/components/validation-message';
import { requestAccessSchema, type RequestAccessValues } from './schemas';
import { SEND_ACCESS_REQUEST_EMAIL } from './graphql';

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
}

export function RequestAccessModal({ open, onClose }: RequestAccessModalProps) {
  const [sendEmail, { loading }] = useMutation(SEND_ACCESS_REQUEST_EMAIL);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RequestAccessValues>({
    resolver: zodResolver(requestAccessSchema),
    mode: 'onChange',
    defaultValues: { name: '', companyName: '', email: '', phoneNumber: '' },
  });

  const submit = handleSubmit(async (values) => {
    await sendEmail({
      variables: {
        distributorAccessRequestInput: {
          name: values.name,
          companyName: values.companyName,
          email: values.email,
          phoneNumber: values.phoneNumber,
        },
      },
    });
    onClose();
  });

  const labelCls = 'sm:col-span-3 text-sm font-medium text-hilevel-slate sm:pt-2';

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <Modal.Header title="Request distributor portal access" onClose={onClose} />
        <Modal.Body className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-12 sm:items-baseline sm:gap-3">
            <label htmlFor="name" className={labelCls}>
              Full name
            </label>
            <div className="sm:col-span-9">
              <TextInput id="name" autoComplete="name" {...register('name')} />
              <ValidationMessage
                error={errors.name as never}
                message={errors.name?.message ?? ''}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 sm:items-baseline sm:gap-3">
            <label htmlFor="companyName" className={labelCls}>
              Company name
            </label>
            <div className="sm:col-span-9">
              <TextInput
                id="companyName"
                autoComplete="organization"
                {...register('companyName')}
              />
              <ValidationMessage
                error={errors.companyName as never}
                message={errors.companyName?.message ?? ''}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 sm:items-baseline sm:gap-3">
            <label htmlFor="email" className={labelCls}>
              Email address
            </label>
            <div className="sm:col-span-9">
              <TextInput
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                {...register('email')}
              />
              <ValidationMessage
                error={errors.email as never}
                message={errors.email?.message ?? ''}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 sm:items-baseline sm:gap-3">
            <label htmlFor="phoneNumber" className={labelCls}>
              Phone number
            </label>
            <div className="sm:col-span-9">
              <TextInput
                id="phoneNumber"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                {...register('phoneNumber')}
              />
              <ValidationMessage
                error={errors.phoneNumber as never}
                message={errors.phoneNumber?.message ?? ''}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={!isValid || loading}>
            {loading ? 'Sending…' : 'Send'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
